import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useHubspotPlant, type HubspotSearchItem } from '@/features/assessments/api/hubspot-search'
import { HubspotPicker } from '@/features/assessments/containers/hubspot-picker'

export type HubspotPlantFieldProps = {
  // The HubSpot plant ID stored on the assessment.
  value: string | null
  onChange: (hubspotId: string | null) => void
  // Plant already known to the caller (e.g. from the create step's deal pick),
  // so it shows without a lookup.
  knownPlant?: HubspotSearchItem | null
  disabled?: boolean
  error?: boolean
}

// The "HubSpot Plant" field: the plants-only picker over a stored plant ID.
export function HubspotPlantField({
  value,
  onChange,
  knownPlant = null,
  disabled,
  error
}: HubspotPlantFieldProps) {
  const { t } = useTranslation('plantAssessment')
  // The last plant picked here; the ID alone cannot render the option.
  const [picked, setPicked] = useState<HubspotSearchItem | null>(null)

  const local = [picked, knownPlant].find(plant => plant && plant.hubspotId === value) ?? null
  const lookup = useHubspotPlant(local ? null : value)
  const plant = local ?? lookup.data ?? (value ? unresolvedPlant(value) : null)

  function handleChange(item: HubspotSearchItem | null) {
    setPicked(item)
    onChange(item?.hubspotId ?? null)
  }

  return (
    <HubspotPicker
      type="plant"
      value={plant}
      onChange={handleChange}
      disabled={disabled}
      error={error}
      label={t('assessmentFormModal.hubspotPlant.label')}
      placeholder={t('assessmentFormModal.hubspotPlant.placeholder')}
    />
  )
}

// A saved ID with no materialized plant (lookup pending, missing or failed):
// show the bare ID rather than an empty field that would hide the reference.
function unresolvedPlant(hubspotId: string): HubspotSearchItem {
  return {
    id: `plant:${hubspotId}`,
    type: 'plant',
    hubspotId,
    title: hubspotId,
    address: null,
    lastActivityAt: null,
    stageId: null,
    plantHubspotId: null,
    plantTitle: null,
    suggestedCountry: null,
    dealOwner: null,
    salesEngineerOwner: null
  }
}
